import React, { Suspense, useCallback } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';

import { useDocChangeContext } from '@confluence/editor-features/entry-points/useDocChangeContext.ts';

import { ReviewContextualNudge } from './ReviewContextualNudge.tsx';
import { useDetectPageWordCount } from './useDetectPageWordCount.ts';
import { useIsReviewNudgePageEligible } from './useIsReviewNudgePageEligible.ts';

const WORD_COUNT_THRESHOLD = 500;

export type ReviewContextualNudgeContext = {
	contentId: string;
	/** The live editor document. The nudge only shows where it exists, which is edit mode. */
	getAdfDocument?: () => Promise<DocNode | null>;
};

type ReviewContextualNudgeEligibilityProps = {
	context?: ReviewContextualNudgeContext;
	onGetSuggestions?: () => void;
	onShown?: () => void;
	onHidden?: () => void;
	subscribeToTargetClick?: (listener: () => void) => () => void;
};

const WordCountEligibleContextualNudge = ({
	context,
	getAdfDocument,
	onGetSuggestions,
	onShown,
	onHidden,
	subscribeToTargetClick,
}: Pick<
	ReviewContextualNudgeEligibilityProps,
	'onGetSuggestions' | 'onShown' | 'onHidden' | 'subscribeToTargetClick'
> & {
	context: ReviewContextualNudgeContext;
	getAdfDocument: () => Promise<DocNode | null>;
}) => {
	const { subscribeDocChange, unsubscribeDocChange } = useDocChangeContext();
	const subscribeToDocumentChange = useCallback(
		(listener: () => void) => {
			// The context hands the listener the editor view, which the nudge doesn't need.
			const handleDocChange = () => listener();
			subscribeDocChange(handleDocChange);
			return () => unsubscribeDocChange(handleDocChange);
		},
		[subscribeDocChange, unsubscribeDocChange],
	);

	// Reads the document and listens to the editor, so it only mounts for users who passed every
	// cheaper check.
	const isOverWordCount = useDetectPageWordCount({
		contentId: context.contentId,
		threshold: WORD_COUNT_THRESHOLD,
		getAdfDocument,
		subscribeToDocumentChange,
	});

	if (!isOverWordCount) {
		return null;
	}

	return (
		<ReviewContextualNudge
			onGetSuggestions={onGetSuggestions}
			onShown={onShown}
			onHidden={onHidden}
			subscribeToTargetClick={subscribeToTargetClick}
		/>
	);
};

// Suspends while it loads and throws on error, so it needs Suspense and an error boundary above it.
const PageEligibleContextualNudge = (
	props: React.ComponentProps<typeof WordCountEligibleContextualNudge>,
) => {
	const isPageEligible = useIsReviewNudgePageEligible(props.context.contentId);

	return isPageEligible ? <WordCountEligibleContextualNudge {...props} /> : null;
};

/**
 * Mounts the contextual nudge only where it suits: in the editor, on a page the current user owns
 * or has contributed to that wasn't created with Rovo, and over the word count threshold. Rendering
 * the Post Office message records an exposure, so anyone who is ineligible must never reach it. The
 * checks run cheapest first, so the word count, which reads the document and listens to editor
 * changes, only runs for users who passed the others. The page check throws on error, so this needs
 * an error boundary above it.
 */
export const ReviewContextualNudgeEligibility = ({
	context,
	onGetSuggestions,
	onShown,
	onHidden,
	subscribeToTargetClick,
}: ReviewContextualNudgeEligibilityProps): React.JSX.Element | null => {
	// Without the live editor document (view mode, published pages) there is nothing to count. The
	// editor is only open for users who can edit, so there is no separate permission check.
	const getAdfDocument = context?.getAdfDocument;
	if (!context?.contentId || !getAdfDocument) {
		return null;
	}

	return (
		<Suspense fallback={null}>
			<PageEligibleContextualNudge
				context={context}
				getAdfDocument={getAdfDocument}
				onGetSuggestions={onGetSuggestions}
				onShown={onShown}
				onHidden={onHidden}
				subscribeToTargetClick={subscribeToTargetClick}
			/>
		</Suspense>
	);
};
